import { all, takeLeading, takeLatest, put, call, select, takeEvery } from 'redux-saga/effects'
import { history } from 'index'
import { getConfig, setEnvParam, getEnvParam, getTempEnvParam, removeTempEnvParam } from 'env'
import { getSearchParams, DEFAULT_AUTH_PAGE } from 'utils'
import { getInternalPath } from 'utils/silentAuth'
import { VAULT_WRITE_FORMAT_KEY } from 'vaultWriteFormat'
import conf, { isGlobal } from 'conf'
import { setPlanContext } from 'utils/plan/tier'
import { getSettings } from 'services/landing'
import reloadPlanLicense from 'services/plan/reloadLicense'
import { getPaidLicense } from 'services/landing/utils/api'
import ResourceService from 'api/Resource'
import { startForwardSweep } from 'services/vaultV2/sweepRunner'
import AuthService from 'api/Auth'
import { getUMKFromLocal, deleteUMKFromLocal } from 'pUtils'
import { makeUserKeySource, makeGroupKeySourceFrom } from 'services/vaultV2/keyAdapters'
// 🛑 **부수 효과 import 다** — `window.__vaultReverse` 를 단다(사고 대응용). 이 import 가
//    없으면 번들에 들어가지 않아 **콘솔에서 닿을 수 없다**(`window.*` 노출 지점 0건, 실측).
//
// 🛑 **운영 빌드에도 붙는다** (PR #2035, 2026-08-27). 초판은 여기에 *"프로덕션 빌드에서는
//    `isDevBuild` 가 false 라 아무것도 붙지 않는다"* 고 적혀 있었으나 그 PR 이 게이트를
//    제거했다. 되돌리기가 반드시 필요한 상황이 **운영에서 배포를 revert 할 때** 뿐이고,
//    열쇠가 언락한 브라우저에만 있어 서버 배치로 대체할 수 없기 때문이다. 판단 근거 전문은
//    `services/vaultV2/reverseTool.js` 머리 주석.
import 'services/vaultV2/reverseTool'

import store from 'store'

import * as landing from 'services/landing'
import * as landingAccount from 'services/landing/account'
import * as landingBasic from 'services/landing/basic'

import { clearAttachmentCache } from 'utils/attachmentCache'
import { clearDeviceKeys } from 'services/passkey/deviceUnlock'
import { notifyExtensionMasterPassword } from 'utils/extensionBridge'
import actions from './actions.ts'

const mapAuthProviders = {
  landing: {
    loginByToken: landing.loginByToken,
    umkLogin: landing.umkLogin,
    umkRegister: landing.umkRegister,
    umkChangePW: landing.umkChangePW,
    currentAccount: landingAccount.currentAccount,
    logout: landing.logout,
    basicRegister: landingBasic.basicRegister,
    basicLogin: landingBasic.basicLogin,
    currentBasicAccount: landingAccount.currentBasicAccount,
    basicInviteInfo: landingBasic.basicInviteInfo,
  },
}

// 일회용 인증 URL — `claim_key` 를 한 번 쓰고 버리므로 "돌아갈 곳" 이 될 수 없다.
// 두 경로가 같은 컴포넌트를 쓴다 (`services/router/index.js:54·58` → `pages/auth/callback`),
// 그 안에서 `pathname.includes('sso/callback')` 으로 갈린다 (`callback/index.js:77`).
const ONE_TIME_AUTH_PATHS = ['/auth/callback', '/auth/sso/callback']

export function* BASIC_LOGIN({ payload }) {
  const { email, password, saveToken, onComplete } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  try {
    const { authProvider: autProviderName } = yield select((state) => state.settings)
    const success = yield call(
      mapAuthProviders[autProviderName].basicLogin,
      email,
      password,
      saveToken,
    )

    if (success) {
      yield INNER_LOAD_CURRENT_ACCOUNT(true)
    }

    yield put({
      type: 'user/SET_STATE',
      payload: {
        loading: false,
      },
    })

    yield put({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'showZeroDarkWeb',
        value: true,
      },
    })
  } finally {
    if (typeof onComplete === 'function') {
      onComplete()
    }
  }
}

export function* SET_RECOVERY_UMK({ payload }) {
  const { recoveryUMK } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: { recoveryUMK },
  })

  yield INNER_LOAD_CURRENT_ACCOUNT(true)
}

export function* SET_RESET_PW({ payload }) {
  const { resetPW } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: { resetPW },
  })
}

export function* BASIC_REGISTER({ payload }) {
  const { request } = payload

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })

  const { authProvider: autProviderName } = yield select((state) => state.settings)
  yield call(mapAuthProviders[autProviderName].basicRegister, request)

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })

  history.push('/masterPW/register')
}

export function* LOGIN_BY_TOKEN({ payload }) {
  const { tokenInfo } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })
  const { authProvider: autProviderName } = yield select((state) => state.settings)
  const result = yield call(mapAuthProviders[autProviderName].loginByToken, tokenInfo)
  if (result === 'success') {
    yield INNER_LOAD_CURRENT_ACCOUNT(true)
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })

  if (result !== 'success') {
    yield history.push(DEFAULT_AUTH_PAGE)
  }
}

export function* SET_WORKSPACE_NAME({ payload }) {
  const { workspaceName } = payload

  const { workspace } = yield select((state) => state.user)
  const { workspaceList } = yield select((state) => state.user)
  const { id: workspaceId } = workspace

  const updatedWorkspaceList = workspaceList.map((value) =>
    value.id === Number(workspaceId) ? { ...value, name: workspaceName } : value,
  )

  yield put({
    type: 'user/SET_STATE',
    payload: {
      workspaceList: updatedWorkspaceList,
      workspace: { id: workspaceId, workspace_name: workspaceName },
    },
  })
}

export function* SET_USER_NAME({ payload }) {
  const { name } = payload

  const safebox = yield select((state) => state.user?.safebox || {})

  yield put({
    type: 'user/SET_STATE',
    payload: {
      name,
      safebox: {
        ...safebox,
        name,
      },
    },
  })
}

export function* SET_USER_AVATAR({ payload }) {
  const { avatar } = payload

  yield put({
    type: 'user/SET_STATE',
    payload: {
      avatar,
    },
  })
}

export function* SELECT_WORKSPACE({ payload }) {
  const { workspace, role, id } = payload
  yield store.set('app.user.workspace_id', workspace.id)
  const { safebox } = yield select((state) => state.user || {})

  const newSafebox = { ...safebox, userId: id, role }

  yield put({
    type: 'user/SET_STATE',
    payload: { workspace, role, id, safebox: newSafebox },
  })

  yield call(setEnvParam, 'appType', JSON.stringify(newSafebox))

  // JP plan_tier: 조회 실패(reject)를 놓치면 이전 워크스페이스 tier 가 잔존하고,
  // rootSaga 가 all() 이라 에러 전파 시 LOGOUT 포함 user 사가 전체가 죽는다 — 삼키고 fail-open.
  let license = null
  try {
    license = yield call(getPaidLicense)
  } catch (e) {
    license = null
  }
  const paidLicense = license?.code === 200 ? license.data : null
  // 실패 시에도 null 로 덮는다 — redux(paidLicense)와 planContext 가 서로 다른
  // 워크스페이스를 가리키는 split-brain 방지
  yield put({
    type: 'user/SET_STATE',
    payload: { paidLicense },
  })
  // 게이팅 헬퍼(utils/plan/tier)에 컨텍스트 주입 — license null = fail-open.
  // 메뉴는 앱 로드 시 license 전에 빌드되므로 tier 반영을 위해 재빌드한다.
  setPlanContext({ isGlobal: isGlobal(), license: paidLicense })
  yield put({ type: 'menu/RELOAD' })

  const { leaked } = getSearchParams()
  if (leaked === 'true') {
    yield put({
      type: 'settings/SET_IS_LEAKED',
      payload: { isLeaked: false },
    })
  }

  yield call(getSettings)
}

export function* UMK_LOGIN({ payload }) {
  const { password, path, preloadedLoginData, unlockMethod } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: true,
    },
  })
  const { authProvider } = yield select((state) => state.settings)
  // preloadedLoginData: Passkey 경로에서 /v1/auth/passkey/login 응답 재사용 — 중복 /v1/auth/login 호출 skip.
  const success = yield call(
    mapAuthProviders[authProvider].umkLogin,
    password,
    preloadedLoginData,
    unlockMethod,
  )

  if (success) {
    console.log(' --- umk login success --- ')
    // 검증된 마스터 비밀번호를 익스텐션으로 동기화 (로그인 비번 ≠ 마스터 비번).
    notifyExtensionMasterPassword(password)

    // 🛑 **`/auth/` 는 복귀 대상이 아니다** (2026-08-27). 인증 진입 경로이지 사용자가 보던
    //    화면이 아니고, 특히 `/auth/callback` 은 `claim_key` 를 한 번 쓰고 버리는 URL 이라
    //    되돌아가면 **아무것도 할 수 없다** — 소비된 키에는 `pages/auth/callback/index.js:138`
    //    이 redirectPath 를 설정하지 않아 "로그인 처리 중" 스피너에 고정된다. 등록 라우트라
    //    `layouts/index.js:307` 의 `!matchesAnyRoute` 구제도 걸리지 않는다.
    //    이 path 는 `case 'change-password'`(그리고 `case 'common_auth_changePW'`)가
    //    `redirectWithPathQuery` 에 넘기는 `alwaysAddPathPrefixes: ['/masterPW','/auth']` 가
    //    만들어 넣는다 — 만드는 쪽은 그대로 두고 **쓰는 쪽에서 막는다.**
    //    🛑 같은 파일 줄 번호를 쓰지 않는다 — 이 주석의 초판이 `(:714·:734)` 였는데
    //    같은 날 다른 수정이 20여 줄을 삽입해 **둘 다 엉뚱한 코드를 가리키게 됐다**(실측).
    // 복귀 경로(?path=)는 내부 경로만 쓴다 — 다른 출처 값은 null 이 되어 아래에서 기본 경로로 대체된다.
    let nextPath = getInternalPath(path)
    if (
      nextPath == null ||
      nextPath.indexOf('/masterPW/') === 0 ||
      nextPath.indexOf('/expired') === 0 ||
      nextPath.indexOf('/auth/') === 0
    ) {
      nextPath = getConfig('defaultPage') || '/'
    }

    // window.dispatchEvent(new CustomEvent('darkWebFetchData'))
    yield history.push(nextPath)

    // 금고 정방향 스윕 (PR 7-a). 언락으로 열쇠를 손에 쥔 이 순간에만 v1 → v2 변환이 가능하다
    // — 서버에는 열쇠가 없다. 설계: `backend/docs/design/vault_forward_sweep/`
    //
    // 🛑 **`yield call` 로 부르지 않는다.** 사가가 await 하면 화면 진입이 스윕만큼 늦어진다.
    //    선례는 `pUtils.js:1768 triggerWrkpSilentFlows`(fire-and-forget + Sentry).
    // 🛑 **`history.push` 뒤에 둔다** — 첫 렌더와 CPU 를 다투지 않게 (spec §7.3).
    // 🛑 **"카나리가 꺼져 있으면 저절로 무동작" 은 결과에만 참이다** (PR 7-a 초판 주석의 오류).
    //    `rewriteV1ToV2`(`forwardSweep.js:61-73`)는 복호·재암호화를 **다 한 뒤에** 결과를 보고
    //    버린다(실측: 카나리 꺼짐 `decrypt=1 encrypt=1`). 그래서 아래 `writeFormat` 게이트가 있다.
    // 🛑 **Sentry 보고를 사가에서 하지 않는다.** `services/restClient` 의 `captureSentryError` 를
    //    쓰면 그 모듈의 `query-string`(ESM) 체인이 사가에 딸려와 **기존 사가 테스트가 파싱
    //    단계에서 깨진다**(실측: `sagas-inner-load.test.js`). Task A0 이 `RecordCardContent` 를
    //    분리한 것과 같은 이유다. 보고는 `sweepRunner` 안에서 leaf 인 `services/sentryLog` 로 한다.
    const rsaObj = getTempEnvParam('rsaObj')

    startForwardSweep({
      resourceService: ResourceService,
      rewriteService: { rewrite: (targets) => ResourceService.rewriteVaultFormat(targets) },
      // 🛑 **쓰기 포맷을 여기서 읽어 넘긴다** (PR 7-a-2 · G1). `sweepRunner` 가 직접 `env` 를
      //    import 하면 `env → service → conf → window` 체인 탓에 그 파일의 node 환경 테스트
      //    19건이 **로드 단계에서** 깨진다(실측 `ReferenceError: window is not defined`,
      //    `conf.js:249`). 여기는 이미 `getTempEnvParam` 을 쓰고 있어(`sagas.js:3`) 새 의존이 없다.
      writeFormat: getTempEnvParam(VAULT_WRITE_FORMAT_KEY),
      // 🛑 **열쇠 2종은 리소스와 별개 스위치다** (PR 7-b · 설계 D1). 리소스 스윕이 켜져 있어도
      //    `sweep_keys` 가 꺼져 있으면 `user_key`·`group_key` 는 손대지 않는다. `user_key` 를
      //    잘못 쓰면 그 사용자는 로그인 자체를 못 하고, 로그인을 못 하니 되돌릴 수단도 없다(G1).
      //    그래서 워크스페이스 단위로 따로 열고 닫는다.
      sweepKeys: rsaObj?.sweepKeys === true,
      userKeySource: makeUserKeySource(rsaObj),
      makeGroupKeySource: makeGroupKeySourceFrom(rsaObj),
    })
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: false,
    },
  })
}

export function* UMK_REGISTER({ payload }) {
  const { password } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: true,
    },
  })

  const { authProvider } = yield select((state) => state.settings)
  const success = yield call(mapAuthProviders[authProvider].umkRegister, password)

  if (success) {
    notifyExtensionMasterPassword(password)
    yield INNER_LOAD_CURRENT_ACCOUNT(true)

    history.push(getConfig('defaultPage'))
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: false,
    },
  })
}

export function* UMK_CHANGEPW({ payload }) {
  // PR3 §7.3: wasTemp(임시계정/복구후 강제변경)는 진입점이 명시 전달. 기본 false(자발적 변경).
  // ESCROW 사용자의 was_temp 변경에서만 recovery 키페어 회전 — voluntary 오판 시 복구 붕괴 방지.
  // fromSettings: Settings 모달의 자발적 변경 진입점. 성공 시 reload/navigation 을 skip 하고
  // onComplete(success) 콜백으로 모달에 결과를 알린다(성공→닫기, 실패→제출상태 해제). 미전달 시
  // 기존 동작(임시계정/복구 후 강제변경) 유지. (콜백 패턴: 동일 파일 LOGIN saga 의 onComplete)
  const {
    password,
    newPassword,
    path,
    reloadAccount,
    wasTemp = false,
    fromSettings = false,
    onComplete,
  } = payload
  yield put({
    type: 'user/SET_STATE',
    payload: {
      umkProcessing: true,
    },
  })

  // try-finally: umkChangePW/후속 로직에서 예외가 나도 umkProcessing 해제 + onComplete 를
  // 반드시 호출 — 미호출 시 모달 isSubmitting 고착(버튼 영구 비활성). (BASIC_LOGIN 패턴)
  let success = false
  try {
    const { authProvider } = yield select((state) => state.settings)
    success = yield call(
      mapAuthProviders[authProvider].umkChangePW,
      password,
      newPassword,
      wasTemp,
      fromSettings,
    )

    if (success) {
      notifyExtensionMasterPassword(newPassword)

      // #1329 ②: 서버는 revoke-on-key-change 로 이 사용자의 디바이스 키를 이미 폐기했으므로,
      // 로컬 IndexedDB 키가 잔존하면 Settings '이 브라우저에서 사용 중' 이 stale 표시된다.
      // 정리 실패는 비차단 — 잠금 화면 진입 시 841 자가치유 경로가 존재.
      try {
        yield call(clearDeviceKeys)
      } catch (e) {
        console.warn('[DBE] post-changePW local device cleanup failed:', e)
      }

      // Settings 진입은 overlay 모달 컨텍스트라 nav/reload 가 부적절 — skip 하고 콜백만.
      if (!fromSettings) {
        if (reloadAccount) {
          yield INNER_LOAD_CURRENT_ACCOUNT(true)
        } else {
          // 🛑 위 UMK_LOGIN 과 **같은 가드다.** 제외 목록이 서로 달라 한쪽만 고치면 다른 쪽에
          //    구멍이 남는다 — 임시계정(`auth_type='change-password'`,
          //    `backend/database/models/user.py:694`)이 실제로 타는 쪽이 여기다.
          //    `/auth/` 를 빼는 이유는 위 주석 참조.
          let nextPath = getInternalPath(path)
          if (
            nextPath == null ||
            nextPath.indexOf('/masterPW/') === 0 ||
            nextPath.indexOf('/auth/') === 0
          ) {
            nextPath = getConfig('defaultPage') || '/'
          }
          yield history.push(nextPath)
        }
      }
    }
  } finally {
    yield put({
      type: 'user/SET_STATE',
      payload: {
        umkProcessing: false,
      },
    })

    // 성공/실패 모두 호출 — 실패 시 모달의 isSubmitting 고착(버튼 영구 비활성) 방지.
    // 에러 팝업은 umkChangePW 가 errorBase 로 이미 표시하므로 여기선 결과 전달만 한다.
    if (fromSettings && typeof onComplete === 'function') {
      onComplete(success)
    }
  }
}

export function* SET_LEAKED_RECORD_COUNT({ payload = {} }) {
  const { leakedCount, callback = true } = payload || {}
  let count = leakedCount != null ? leakedCount : null

  try {
    const { showZeroDarkWeb } = yield select((state) => state.settings)
    if (showZeroDarkWeb === true) {
      if (leakedCount == null && callback === true) {
        const response = yield call(ResourceService.getLeaked)
        // console.log(' -- showZeroDarkWeb : ', showZeroDarkWeb)
        count = response.total
        // return response.total
      }

      yield put({
        type: 'user/SET_STATE',
        payload: { leakedCount: count },
      })

      if (count === 0) {
        yield put({
          type: 'settings/SET_IS_LEAKED',
          payload: { isLeaked: false },
        })
      }

      return count
    }

    return null
  } catch (error) {
    console.error('❌ Error fetching leaked data:', error)
    return null
  }
}

function* redirectWithPathQuery(
  curPath,
  {
    targetPath,
    defaultAddPath = false,
    skipPathPrefixes = [],
    alwaysAddPathPrefixes = [],
    alwaysSamePath = false,
  },
) {
  if (alwaysSamePath) {
    yield history.push(targetPath)
    return
  }

  const shouldAlwaysAddPath = alwaysAddPathPrefixes.some((prefix) => curPath.indexOf(prefix) === 0)
  const shouldSkipPath = skipPathPrefixes.some((prefix) => curPath.indexOf(prefix) === 0)

  if (shouldAlwaysAddPath) {
    yield history.push(`${targetPath}?path=${curPath}`)
  } else if (shouldSkipPath) {
    yield history.push(targetPath)
  } else if (defaultAddPath) {
    yield history.push(`${targetPath}?path=${curPath}`)
  } else {
    yield history.push(targetPath)
  }
}

function* INNER_LOAD_CURRENT_ACCOUNT(useByLogin) {
  try {
    const { authProvider } = yield select((state) => state.settings)
    const resetPW = yield select((state) => state.user.resetPW)
    const { pathname } = window.location
    const { search } = window.location
    const token = new URLSearchParams(search).get('token')

    // viewer, privacy 엔드포인트는 계정 로딩 및 마스터 비밀번호 redirect 건너뛰기
    if (pathname.startsWith('/viewer') || pathname.startsWith('/privacy')) {
      return
    }

    if (pathname.startsWith('/invite/accept') && token != null) {
      const res = yield call(mapAuthProviders[authProvider].basicInviteInfo, token)

      if (store.get('app.user.token') != null) {
        yield put({ type: 'user/LOGOUT' })
      }

      // SAML 초대: LOGOUT 전에 먼저 IdP로 리다이렉트 (중간 화면 전환 방지)
      if (
        res?.code === 200 &&
        res.data?.auth_type === 'saml' &&
        res.data?.invitee_email &&
        (res.data.flow_type === 'signup' || res.data.flow_type === 'register')
      ) {
        try {
          // callback 복귀 시 invite 정보를 참조할 수 있도록 미리 저장
          yield setEnvParam('inviteType', JSON.stringify(res.data))
          yield store.set('app.user.invite_token', token)

          const discoverResult = yield call(AuthService.discoverSaml, res.data.invitee_email)
          if (discoverResult.code === 200 && discoverResult.data?.sso_login_url) {
            const rawUrl = discoverResult.data.sso_login_url
            const ssoLoginUrl = rawUrl.replace('/api/v1/', '/')
            const { authURL } = conf.SERVER
            const separator = ssoLoginUrl.includes('?') ? '&' : '?'
            window.location.href = `${authURL}${ssoLoginUrl}${separator}login_hint=${encodeURIComponent(res.data.invitee_email)}`
            return
          }
        } catch (e) {
          console.error('SAML discover failed, falling back to default flow', e)
        }
      }

      // SAML이 아니거나 discover 실패 시 기존 플로우 진행
      if (store.get('app.user.token') != null) {
        yield put({ type: 'user/LOGOUT' })
      }

      if (res?.code === 200) {
        console.log(' --- basicInviteInfo res --- ', res)
        const { data } = res

        switch (data.flow_type) {
          case 'signup':
            yield setEnvParam('inviteType', JSON.stringify(data))
            yield store.set('app.user.invite_token', token)

            yield history.push('/auth/signup/register')
            return

          case 'register':
            yield setEnvParam('inviteType', JSON.stringify(data))
            yield store.set('app.user.invite_token', token)
            yield history.push(DEFAULT_AUTH_PAGE)

            return
          // case 'join':
          //   yield history.push(DEFAULT_AUTH_PAGE)
          //   return
          default:
            yield history.push(DEFAULT_AUTH_PAGE)
            return
        }
      } else {
        yield history.push(DEFAULT_AUTH_PAGE)
        return
      }
    }

    const response = yield call(
      isGlobal()
        ? mapAuthProviders[authProvider].currentBasicAccount
        : mapAuthProviders[authProvider].currentAccount,
    )
    if (response) {
      const {
        id,
        email,
        name,
        avatar,
        role,
        serverType,
        workspace,
        provisioning,
        authType,
        provider,
        workspaceList,
        paidLicense,
        recovery_option: recoveryOption,
        wrkp_initialized: wrkpInitialized,
        // 초대(register) ESCROW escrow 봉인용 워크스페이스 복구 공개키 — currentAccount/currentBasicAccount
        // 가 auth/info 에서 read(landing/account Fix C). 이 값을 redux 로 전달하지 않으면
        // RegisterMasterPW 가 escrow 봉인 재료를 못 받아 escrowed_recovery_key=null 로 전송됨.
        wrkp_public_key: wrkpPublicKey,
      } = response
      yield put({
        type: 'user/SET_STATE',
        payload: {
          id,
          name,
          email,
          avatar,
          role,
          authorized: true,
          serverType,
          workspace,
          provisioning,
          workspaceList,
          authType,
          provider,
          paidLicense,
          recovery_option: recoveryOption,
          wrkp_initialized: wrkpInitialized,
          wrkp_public_key: wrkpPublicKey,
        },
      })

      // JP plan_tier: 게이팅 컨텍스트 주입 — 바로 아래 menu/RELOAD 가 tier 반영 재빌드까지 겸한다
      setPlanContext({ isGlobal: isGlobal(), license: paidLicense || null })

      yield put({
        type: 'menu/RELOAD',
      })

      let curPath = window.location.pathname + window.location.search

      if (
        useByLogin &&
        (curPath.indexOf('/masterPW/register') === 0 ||
          curPath.indexOf('/masterPW/changePW') === 0 ||
          curPath.indexOf('/auth/signup/register') === 0)
      ) {
        const params = getSearchParams()
        const defaultPage = getConfig('defaultPage') || '/'
        // 기억해 둔 경로(?path=)도 내부 경로만 쓴다 — 아니면 기본 경로로 대체한다.
        const remembered =
          (params.path != null && getInternalPath(window.decodeURIComponent(params.path))) ||
          defaultPage

        // 🛑 **일회용 인증 URL 은 복귀 대상이 아니다** (2026-08-27).
        //    `/auth/callback?claim_key=X`(SAML 은 `/auth/sso/callback`)는 키를 한 번 쓰고
        //    버리는 주소다. 그런데 `case 'change-password'`(그리고 `case 'common_auth_changePW'`)
        //    가 `redirectWithPathQuery` 에 넘기는
        //    `alwaysAddPathPrefixes: ['/masterPW','/auth']` 가 그 주소를 `?path=` 에 심어 두고,
        //    변경이 끝나면 이 함수 말미의 `if (success === true)` → `history.push(curPath)` 가
        //    거기로 되돌려보낸다. 소진된 키에는
        //    `pages/auth/callback/index.js:138` 이 탈출 경로를 정하지 않아 "로그인 처리 중"
        //    화면에 고정된다(등록 라우트라 `layouts/index.js:307` 의 `!matchesAnyRoute`
        //    구제도 안 걸린다).
        //
        //    🛑 **소비 지점(말미의 `history.push(curPath)`)이 아니라 여기서 막는다.**
        //    그쪽은 `useByLogin` 게이트 **밖**이라 이 함수의 호출처 6곳이 전부 지나가고,
        //    앱 부팅이 **실제로** `/auth/callback` 에 있는 동안
        //    거기를 떠나게 만든다. 이 블록은 "기억해 둔 곳"만 다루므로 범위가 정확하다.
        //    설계: `frontend/docs/design/auth_callback_return_path/`
        curPath = ONE_TIME_AUTH_PATHS.some((prefix) => remembered.indexOf(prefix) === 0)
          ? defaultPage
          : remembered
      }

      let success = false
      console.log(' --- authType : ', authType, ' resetPW : ', resetPW, ' curPath : ', curPath)
      if (resetPW === true) {
        if (curPath.indexOf('/masterPW/verify/changePW') === 0) {
          // 🔥 뒤로가기 시 masterPW/verify로 이동하게 수정
          const previousPage = document.referrer || '' // 이전 페이지 가져오기

          if (previousPage.includes('/masterPW/verify')) {
            yield history.push('/masterPW/verify')
          } else {
            yield history.push('/masterPW/verify/changePW')
          }
        } else if (curPath.indexOf('/masterPW/verify') === 0) {
          yield history.push('/masterPW/verify')
        } else {
          yield history.push(`/masterPW/verify/changePW?path=${curPath}`)
        }
      } else if (curPath.indexOf('/masterPW/verify') === 0) {
        yield history.push('/masterPW/verify')
      } else {
        switch (authType) {
          case 'login':
            if (getTempEnvParam('inviteRegisterLogin') === true) {
              yield history.push('/')
              yield removeTempEnvParam('inviteRegisterLogin')
            } else {
              const umk = yield call(getUMKFromLocal, true)
              const noLogin = yield select((state) => state.user.noLogin)
              if (umk != null && noLogin === false) {
                // 세션 자동 확인 라벨은 **앱 부트 경로에만** 붙인다. 이 함수의 호출처가 6곳이고
                // 마스터 비밀번호 등록(UMK_REGISTER)·변경(UMK_CHANGEPW) 직후도 여기로 오는데,
                // 그 행을 "세션 자동 확인"으로 적으면 사실이 아닌 감사 문장이 된다(설계 D16).
                // useByLogin=false 인 LOAD_CURRENT_ACCOUNT 만 라벨 대상이다.
                // 2번째 인자는 preloadedLoginData 자리 — null 을 명시로 끼운다.
                success = yield call(
                  mapAuthProviders[authProvider].umkLogin,
                  umk,
                  null,
                  useByLogin ? undefined : 'session_restore',
                )
              }

              if (success === false) {
                const { path } = getSearchParams()

                const isAuthPage =
                  curPath.startsWith('/masterPW/login') || curPath.startsWith('/auth/')
                const isIpBlockedPage = curPath.startsWith('/ip-blocked')

                const pathToUse = isAuthPage || isIpBlockedPage ? path : path || curPath
                const redirectPath = pathToUse
                  ? `/masterPW/login?path=${encodeURIComponent(pathToUse)}`
                  : '/masterPW/login'

                yield history.push(redirectPath)
              }
            }

            break
          case 'signup':
          case 'register':
            yield redirectWithPathQuery(curPath, {
              targetPath: '/masterPW/register',
              defaultAddPath: true,
              skipPathPrefixes: ['/masterPW/register', '/auth/'],
            })
            break
          case 'change-password':
            yield redirectWithPathQuery(curPath, {
              targetPath: '/masterPW/changePW',
              defaultAddPath: false,
              skipPathPrefixes: ['/masterPW/changePW'],
              alwaysAddPathPrefixes: ['/masterPW', '/auth'],
            })
            break

          case 'expired': {
            const expiredData = getEnvParam('expiredData')
            if (expiredData != null) {
              yield redirectWithPathQuery(curPath, {
                targetPath: '/expired',
                alwaysSamePath: true,
              })
            }
            break
          }

          case 'common_auth_changePW':
            yield redirectWithPathQuery(curPath, {
              targetPath: '/auth/changePW',
              defaultAddPath: false,
              skipPathPrefixes: ['/auth/changePW'],
              alwaysAddPathPrefixes: ['/masterPW', '/auth'],
            })
            break

          case 'ip-blocked': {
            deleteUMKFromLocal()
            yield history.push('/ip-blocked')
            if (store.get('app.user.token') != null) {
              yield call(mapAuthProviders[authProvider].logout)
            }
            yield call(clearAttachmentCache)
            return
          }

          default:
        }
      }

      if (success === true) {
        // expiredData가 로컬 스토리지에 없으면 /expired 페이지로 이동하지 않음
        if (curPath.indexOf('/expired') === 0) {
          const expiredData = getEnvParam('expiredData')
          if (expiredData == null) {
            const defaultPage = getConfig('defaultPage') || '/'
            yield history.push(defaultPage)
            return
          }
        }
        yield history.push(curPath)
      }
    }
  } catch (error) {
    console.log(error)

    yield put({
      type: 'user/SET_STATE',
      payload: {
        authorized: false,
      },
    })
  }
}

export function* LOAD_CURRENT_ACCOUNT() {
  // viewer 엔드포인트는 계정 로딩 건너뛰기
  const { pathname } = window.location
  if (pathname.startsWith('/viewer')) {
    yield put({
      type: 'user/SET_STATE',
      payload: {
        loading: false,
      },
    })
    return
  }

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: true,
    },
  })

  yield INNER_LOAD_CURRENT_ACCOUNT(false)

  yield put({
    type: 'user/SET_STATE',
    payload: {
      loading: false,
    },
  })
}

export function* LOGOUT(action) {
  // JP plan_tier: 게이팅 컨텍스트 초기화 (미주입 = fail-open — 로그인 화면에선 무해)
  setPlanContext(null)

  const { authProvider } = yield select((state) => state.settings)
  const fromExtension = action?.payload?.fromExtension === true
  const fromBroadcast = action?.payload?.fromBroadcast === true
  const shouldBroadcast = action?.payload?.broadcast === true

  // 다중 탭 동기 로그아웃: 사용자가 *직접 누른* 로그아웃만 다른 탭으로 전파한다
  // (broadcast 플래그 opt-in). 로그인/초대 흐름 내부에서 cleanup 용으로 dispatch
  // 되는 LOGOUT (invite accept, landing account, 팝업 차단 등) 은 이 플래그가 없어
  // 전파되지 않으므로, 한 탭의 로그인이 다른 탭을 auth/login 으로 튕기지 않는다.
  // BroadcastChannel 은 송신자 자신에게는 전달되지 않으므로 자가-로그아웃 루프 없음.
  if (shouldBroadcast) {
    try {
      const bc = new BroadcastChannel('user-logout')
      bc.postMessage({ type: 'user-logout' })
      bc.close()
    } catch (e) {
      // BroadcastChannel 미지원 환경은 무시
    }
  }

  // 익스텐션 / 다른 탭 broadcast 트리거: 서버 로그아웃 API 는 트리거한 탭이 이미
  // 호출했으므로 skipApi 로 로컬 cleanup 만 수행한다. UI 가 한 박자 늦지 않도록
  // state + 토큰을 동기로 정리한다. layouts 의 isUserAuthorized = authorized ||
  // token != null 가드라 둘 다 비워야 즉시 redirect. 나머지 app.user.* /
  // orgChartDB cleanup 은 아래 landing.logout 에서 이어서 수행.
  if (fromExtension || fromBroadcast) {
    store.remove('app.user.token')
    store.remove('app.user.refreshToken')
    yield put({
      type: 'user/SET_STATE',
      payload: {
        id: '',
        name: '',
        role: '',
        email: '',
        avatar: '',
        authorized: false,
        loading: false,
        resetPW: false,
        workspace: null,
        provisioning: false,
        recoveryUMK: false,
        // 재설정 OTP 자가 발송 플래그 — 로그아웃(계정 전환) 시 이전 값이 남지 않도록 초기화 (설계 D6).
        // 워크스페이스 전환(SELECT_WORKSPACE)에는 두지 않는다: KR 은 1인 1워크스페이스(backend
        // common/single_workspace_guard.py)라 전환 경로가 없고, 비KR 은 플래그가 항상 true 라 값이 같다.
        can_request_reset_otp: null,
        error_shown: true,
        verificationEmail: '',
        workspaceList: [],
        leakedCount: null,
      },
    })
  }

  // 실패는 삼킨다 — 여기서 throw 가 새면 rootSaga all() 이 user 사가 전체를 취소하고
  // 아래 로컬 정리(SET_STATE)도 건너뛴다(운영: orgChartDB store 없음 → logout 의 clear 실패).
  try {
    yield call(mapAuthProviders[authProvider].logout, {
      skipApi: fromExtension || fromBroadcast,
    })
  } catch (error) {
    console.log('logout error', error)
  }
  yield call(clearAttachmentCache)
  yield put({
    type: 'user/SET_STATE',
    payload: {
      id: '',
      name: '',
      role: '',
      email: '',
      avatar: '',
      authorized: false,
      loading: false,
      resetPW: false,
      workspace: null,
      provisioning: false,
      recoveryUMK: false,
      // 재설정 OTP 자가 발송 플래그 — 로그아웃(계정 전환) 시 이전 값이 남지 않도록 초기화 (설계 D6).
      // 워크스페이스 전환(SELECT_WORKSPACE)에는 두지 않는다: KR 은 1인 1워크스페이스(backend
      // common/single_workspace_guard.py)라 전환 경로가 없고, 비KR 은 플래그가 항상 true 라 값이 같다.
      can_request_reset_otp: null,
      error_shown: true,
      verificationEmail: '',
      workspaceList: [],
      leakedCount: null,
      // PR-F: 로그아웃 시 종료코드 리셋 → 재로그인 후에도 여전히 만료면 restClient가 재감지(가드 해제)
      licenseTerminated: null,
    },
  })
}

// 플랜(요금제) 재조회 — 포털에서 등급·축이 바뀐 뒤 새로고침을 안 한 탭의 stale 라이선스
// 갱신용. 서버는 매 요청 DB 로 판정하므로 정책 자체는 이미 안전하고(우회 불가), 이 saga 는
// **UI 게이트와 안내를 서버 정책에 맞추는 것**이 목적이다 — 갱신 후 사용자는 "왜 막혔는지"를
// 상한 표시·메뉴 상태로 알 수 있고, 같은 헛수고(대용량 업로드 후 거절)를 반복하지 않는다.
// 실패는 삼킨다(fail-open) — 이 경로가 죽으면 rootSaga all() 이 user 사가 전체를 내린다.
export function* RELOAD_PAID_LICENSE() {
  // 구현은 services/plan/reloadLicense 하나로 모은다 — single-flight·쓰로틀·로그아웃·
  // 워크스페이스 전환 가드가 거기 있고, 여기에 사본을 두면 그 가드를 우회하는 트리거가
  // 생긴다(실제로 요청 폭주와 워크스페이스 교차 오염이 났다, 2026-08-26).
  // 실패는 헬퍼가 삼킨다 — 이 경로가 죽으면 rootSaga all() 이 user 사가 전체를 내린다.
  yield call(reloadPlanLicense)
}

export default function* rootSaga() {
  yield all([
    takeLeading(actions.LOGIN_BY_TOKEN, LOGIN_BY_TOKEN),
    takeLeading(actions.SET_RESET_PW, SET_RESET_PW),
    takeLatest(actions.LOAD_CURRENT_ACCOUNT, LOAD_CURRENT_ACCOUNT),
    takeLeading(actions.LOGOUT, LOGOUT),
    takeEvery(actions.UMK_LOGIN, UMK_LOGIN),
    takeLeading(actions.UMK_REGISTER, UMK_REGISTER),
    takeLeading(actions.UMK_CHANGEPW, UMK_CHANGEPW),
    takeLeading(actions.BASIC_REGISTER, BASIC_REGISTER),
    takeLeading(actions.BASIC_LOGIN, BASIC_LOGIN),
    takeLeading(actions.SELECT_WORKSPACE, SELECT_WORKSPACE),
    takeLeading(actions.SET_WORKSPACE_NAME, SET_WORKSPACE_NAME),
    takeLeading(actions.SET_USER_NAME, SET_USER_NAME),
    takeLeading(actions.SET_USER_AVATAR, SET_USER_AVATAR),
    takeLeading(actions.SET_LEAKED_RECORD_COUNT, SET_LEAKED_RECORD_COUNT),
    // takeLatest: 연속 거부(멀티 파일 업로드 등)에 마지막 응답만 반영
    takeLatest(actions.RELOAD_PAID_LICENSE, RELOAD_PAID_LICENSE),
    LOAD_CURRENT_ACCOUNT(), // run once on app load to check user auth
  ])
}
